import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ProblemCard = ({ problem, onApply, onFavorite, onViewDetails, isFavorited = false, hasApplied = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getComplexityColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'bg-success text-success-foreground';
      case 'intermediate': return 'bg-warning text-warning-foreground';
      case 'advanced': return 'bg-error text-error-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getTechIcon = (tech) => {
    const techIcons = {
      'IoT': 'Cpu',
      'AI': 'Brain',
      'GIS': 'Map',
      'Data Science': 'BarChart3',
      'Smart City Design': 'Building2'
    };
    return techIcons?.[tech] || 'Code';
  };

  const formatDeadline = (date) => {
    const deadline = new Date(date);
    const now = new Date();
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return 'Due Tomorrow';
    return `${diffDays} days left`;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft hover:shadow-elevated transition-all duration-300 overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Building2" size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg">{problem?.title}</h3>
              <p className="text-muted-foreground text-sm">{problem?.cityName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onFavorite(problem?.id)}
            className="transition-micro"
          >
            <Icon 
              name={isFavorited ? "Heart" : "Heart"} 
              size={20} 
              className={isFavorited ? "text-error fill-current" : "text-muted-foreground"} 
            />
          </Button>
        </div>

        {/* Problem Preview */}
        <p className="text-foreground text-sm mb-4 line-clamp-3">
          {problem?.description}
        </p>

        {/* Tags and Metadata */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(problem?.complexity)}`}>
            {problem?.complexity}
          </span>
          <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
            {problem?.teamSize} members
          </span>
          <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
            {problem?.timeline}
          </span>
        </div>

        {/* Technology Focus Areas */}
        <div className="flex flex-wrap gap-2 mb-4">
          {problem?.techFocus?.map((tech, index) => (
            <div key={index} className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs">
              <Icon name={getTechIcon(tech)} size={14} />
              <span>{tech}</span>
            </div>
          ))}
        </div>

        {/* Required Skills */}
        <div className="mb-4">
          <p className="text-sm font-medium text-foreground mb-2">Required Skills:</p>
          <div className="flex flex-wrap gap-1">
            {problem?.requiredSkills?.slice(0, 4)?.map((skill, index) => (
              <span key={index} className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">
                {skill}
              </span>
            ))}
            {problem?.requiredSkills?.length > 4 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                +{problem?.requiredSkills?.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Deadline */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>Deadline: {new Date(problem.deadline)?.toLocaleDateString()}</span>
          </div>
          <span className={`text-xs font-medium ${
            formatDeadline(problem?.deadline)?.includes('Expired') ? 'text-error' :
            formatDeadline(problem?.deadline)?.includes('Today') || formatDeadline(problem?.deadline)?.includes('Tomorrow') ? 'text-warning' :
            'text-success'
          }`}>
            {formatDeadline(problem?.deadline)}
          </span>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-4 border-t border-border bg-muted/30">
          <div className="pt-4 space-y-4">
            {/* Full Description */}
            <div>
              <h4 className="font-medium text-foreground mb-2">Problem Description</h4>
              <p className="text-sm text-foreground leading-relaxed">{problem?.fullDescription}</p>
            </div>

            {/* Deliverables */}
            <div>
              <h4 className="font-medium text-foreground mb-2">Expected Deliverables</h4>
              <ul className="space-y-1">
                {problem?.deliverables?.map((deliverable, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm text-foreground">
                    <Icon name="CheckCircle2" size={14} className="text-success" />
                    <span>{deliverable}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* City Contact */}
            <div>
              <h4 className="font-medium text-foreground mb-2">City Contact</h4>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-foreground">
                    {problem?.cityContact?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{problem?.cityContact?.name}</p>
                  <p className="text-xs text-muted-foreground">{problem?.cityContact?.title}</p>
                </div>
              </div>
            </div>

            {/* All Required Skills */}
            <div>
              <h4 className="font-medium text-foreground mb-2">All Required Skills</h4>
              <div className="flex flex-wrap gap-1">
                {problem?.requiredSkills?.map((skill, index) => (
                  <span key={index} className="px-2 py-1 bg-accent/10 text-accent text-xs rounded">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="p-6 pt-0 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
        >
          {isExpanded ? 'Show Less' : 'View Details'}
        </Button>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(problem?.id)}
            iconName="ExternalLink"
            iconPosition="right"
          >
            Full Details
          </Button>
          <Button
            variant={hasApplied ? "secondary" : "default"}
            size="sm"
            onClick={() => onApply(problem?.id)}
            disabled={hasApplied || formatDeadline(problem?.deadline)?.includes('Expired')}
            iconName={hasApplied ? "Check" : "Send"}
            iconPosition="left"
          >
            {hasApplied ? 'Applied' : 'Apply Now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProblemCard;